import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { DataImportProjectReviewStatus, MutationRefetchQueries } from "~/common/types";
import {
  UpdateDataImportProjectDocument,
  type UpdateDataImportProjectMutation,
  type UpdateDataImportProjectMutationVariables,
} from "~/features/dataImportProjects/gqls";
import type { DataImportProjectFormData } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseDataImportProjectEditProps {
  projectId: string;
  refetchQueriesOnEdit: MutationRefetchQueries;
  onSuccess: () => void;
}

export function useDataImportProjectEdit({
  projectId,
  refetchQueriesOnEdit,
  onSuccess,
}: UseDataImportProjectEditProps) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const { tenantId } = useTenant();
  const [updateDataImportProject] = useMutation<
    UpdateDataImportProjectMutation,
    UpdateDataImportProjectMutationVariables
  >(UpdateDataImportProjectDocument, {
    refetchQueries: refetchQueriesOnEdit,
  });

  const submit = useCallback(
    async (formData: DataImportProjectFormData) => {
      const progressToastId = toast.loading(t("ui.processing"));
      setProcessing(true);

      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        await updateDataImportProject({
          variables: {
            input: {
              tenantId,
              id: projectId,
              name: formData.name,
              statusId: formData.statusId as DataImportProjectReviewStatus,
              documentUrl: formData.documentUrl,
              description: formData.description,
            },
          },
        });

        toast.success(
          t("feature.common.updatedSuccessfully", {
            model: "dataImportProject",
          }),
        );
        onSuccess();
      } catch (e) {
        if (e instanceof Error) {
          toast.error(translateError(t, e));
        }
      } finally {
        toast.dismiss(progressToastId);
        setProcessing(false);
      }
    },
    [updateDataImportProject, projectId, onSuccess, t, tenantId],
  );

  return {
    processing,
    submit,
  };
}
